import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService, HiddenModalButtonComponent } from '@ebw-monorepo/modal';
import { API_URLS, ApiUrlService, SplitViewService } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, lastValueFrom, Subject, switchMap, takeUntil } from 'rxjs';
import { MenuItem } from '../../../../interfaces/dynamicMenu.interface';
import { FeatureSelectService } from '../../../../services/feature-select.service';
import { FilesystemService } from '../../../../services/filesystem.service';
import { MapService } from '../../../../services/map.service';
import { PsaService } from '../../../../services/psa.service';
import { isChecklistElementType } from '../../../../utils/checklist.utils';
import { AttachActionComponent } from '../attach-action/attach-action.component';
import { AttachEventComponent } from '../attach-event/attach-event.component';
import { CheckListComponent } from '../check-list/check-list.component';
import { DetailNodeComponent } from '../detail-node/detail-node.component';
import { RiskMatrixComponent } from '../risk-matrix/risk-matrix.component';
import { UpdateEventProbabilitiesComponent } from '../update-event-probabilities/update-event-probabilities.component';
import { UpdateRelActionValueComponent } from '../update-rel-action-value/update-rel-action-value.component';

@Component({
  selector: 'ebw-monorepo-psa-viewer',
  templateUrl: './psa-object-viewer.component.html',
  styleUrls: ['./psa-object-viewer.component.scss']
})
export class PsaObjectViewerComponent implements OnInit, OnDestroy {
  @Input() id;
  psaObject;
  events;
  /** Element types with a checklist: the "Checklist" button is shown only for elements of these types */
  checklistElementTypes: string[] = [];

  private _destroy$ = new Subject<void>();

  statoAvanzamento$ = new Subject<void>();
  public percentuale_avanzamento = '-';

  indiceRischio$ = new Subject<void>();
  public indice_rischio = '-';

  menuConfig: MenuItem[] = null;

  // ------------------------------------------------------------------------------------------------------------------------------------------

  constructor(
    private dialog: MatDialog,
    private _psaService: PsaService,
    private _ebwModalService: EbwModalService,
    private _splitViewService: SplitViewService,
    private filesystemService: FilesystemService,
    private _mapService: MapService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private _featureSelectService: FeatureSelectService,
    private _apiUrlService: ApiUrlService
  ) {}

  // ------------------------------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._loadPsaObject();
    this._psaService
      ._getChecklistElementTypes()
      .pipe(takeUntil(this._destroy$))
      .subscribe((elementTypes) => (this.checklistElementTypes = elementTypes));
    // se il nodo viene modificato/eliminato da un altro punto dell'app (es. il menu dell'albero elementi), aggiorno questo pannello
    this._psaService.nodeChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => this._loadPsaObject(true));
    this._psaService.checklistChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => this.loadStatoAvanzamento());
    this.observeChangeOnAvanzammentoAndRischio();

    this.loadStatoAvanzamento();
    this.loadIndiceRischio();

    this.menuConfig = [
      // pulsante delete interconnection (solo icona)
      {
        labelKey: 'map.sidebar.psa.actions.delete_interconnection',
        icon: 'delete',
        tooltipKey: 'map.sidebar.psa.actions.delete_interconnection',
        disabled: false,
        render: this.psaObject?.type === 'interconnessione',
        action: () => this.deleteInterconnection()
      },

      // pulsante create interconnection (solo icona)
      {
        labelKey: 'map.sidebar.psa.actions.create_interconnection',
        icon: 'share',
        tooltipKey: 'map.sidebar.psa.actions.create_interconnection',
        disabled: false,
        render: true,
        action: () => this.openCreateInterconnectionPanel()
      },

      // pulsante apri file system (solo icona)
      {
        labelKey: 'map.sidebar.psa.documents',
        icon: 'inventory',
        tooltipKey: 'map.sidebar.psa.documents',
        render: true,
        action: () => this.openElementFileSystem()
      }
    ];
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  observeChangeOnAvanzammentoAndRischio() {
    this.statoAvanzamento$
      .pipe(
        debounceTime(1000),
        switchMap(() => this._psaService._getStatoAvanzamento(this.id))
      )
      .subscribe({
        next: (resp: any) => {
          this.percentuale_avanzamento = resp[0]?.percentuale_avanzamento;
          this.cdr.detectChanges();
        }
      });
    this.indiceRischio$
      .pipe(
        debounceTime(1000),
        switchMap(() => this._psaService._getMatriceRischio(this.id))
      )
      .subscribe({
        next: (resp: any) => {
          this.indice_rischio = resp[0]?.matrice_rischio_tot;
          this.cdr.detectChanges();
        }
      });
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  async openElementFileSystem() {
    const elementFolder = await lastValueFrom(this.filesystemService.checkElementFolder(this.psaObject));
    this.filesystemService.openFilesystemModal(elementFolder);
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  loadStatoAvanzamento() {
    this.statoAvanzamento$.next();
  }
  // ------------------------------------------------------------------------------------------------------------------------------------------

  loadIndiceRischio() {
    this.indiceRischio$.next();
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  private _loadPsaObject(refreshMapFeature = false) {
    this._psaService
      ._getDetail(this.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((resp) => {
        this.events = resp.psa.events?.map((e) => ({
          ...e,
          actions: this.getActionsOfEvent(e, resp.psa.actions)
        }));
        this.psaObject = { ...resp };
        delete this.psaObject.psa;

        if (refreshMapFeature && resp.lat != null && resp.lon != null) {
          const geom4326 = { type: 'Point', coordinates: [resp.lon, resp.lat] };
          const featureCollection = {
            type: 'FeatureCollection',
            features: [
              {
                id: resp.urn,
                type: 'Feature',
                geometry: geom4326,
                properties: { ...resp, geom: geom4326 }
              }
            ]
          };
          this._featureSelectService.featuresSelected = [];
          this._featureSelectService.parseSelectedFeatures(featureCollection, this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN));
          this._featureSelectService.notifyFeaturesSelectedChange();
        }
      });
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  async updateBulkMatriceRischio() {
    this._ebwModalService
      .openCustomModal(
        {
          title: 'Matrice di Rischio',
          content: ''
        },
        RiskMatrixComponent,
        {
          width: '95%',
          height: '95%',
          panelClass: ['custom-dialog-container', 'risk-matrix-dialog-container'],
          disableClose: true,
          hasBackdrop: false,
          data: {
            entityId: this.id,
            entityLabel: this.psaObject?.search_term,
            tipologia: this.psaObject?.tipologia,
            events: this.events
          }
        },
        // componente vuoto solo per evitare il rendering del bottone "Chiudi" di default
        // (RiskMatrixComponent ha già i suoi bottoni Salva/Chiudi nel footer)
        HiddenModalButtonComponent
      )
      .afterClosed()
      .subscribe((result) => {
        if (result === 'UPDATED') {
          this._loadPsaObject();
          this.loadIndiceRischio();
          this.loadStatoAvanzamento();
        }
      });
  }

  deleteAllEvents() {
    this._ebwModalService
      .openConfirmModal({
        title: this.translateService.instant('common.delete'),
        content: this.translateService.instant('map.sidebar.psa.risk_event.confirm_delete_rel_event')
      })
      .afterClosed()
      .subscribe((result) => {
        if (result == true) {
          const eventsToDelete = this.events.map((e) => ({
            evento_rischio_id: e.evento_rischio_id,
            schema_idropotabile_id: e.schema_idropotabile_id
          }));

          this._psaService._removeMultipleEventsFromEntity({ events: eventsToDelete }).subscribe((resp) => {
            this._loadPsaObject();
            this.loadIndiceRischio();
            this.loadStatoAvanzamento();
            this._mapService.clearLayerCache();
          });
        }
      });
  }

  deleteAllActions(evento) {
    if (evento.actions && evento.actions.length > 0)
      evento.actions.forEach((azione) => {
        this._psaService
          ._removeActionFromEntity({
            rel_entity_evento_rischio_id: azione.rel_entity_evento_rischio_id,
            azione_mitigatrice_id: azione.azione_mitigatrice_id
          })
          .subscribe((resp) => {
            this._loadPsaObject();
            this.loadIndiceRischio();
            this._mapService.clearLayerCache();
          });
      });
  }

  goToUpdateEventProbability(evento) {
    const ref = this.dialog.open(UpdateEventProbabilitiesComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '40%',
      data: evento
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp === 'UPDATED') {
        this._loadPsaObject();
        this.loadIndiceRischio();
        this.loadStatoAvanzamento();
        this._mapService.clearLayerCache();
      }
    });
  }

  deleteEvent(evento) {
    this._ebwModalService
      .openConfirmModal({
        title: this.translateService.instant('common.delete'),
        content: this.translateService.instant('map.sidebar.psa.risk_event.confirm_delete_rel_event')
      })
      .afterClosed()
      .subscribe((result) => {
        if (result == true) {
          this._psaService
            ._removeEventFromEntity({
              evento_rischio_id: evento.evento_rischio_id,
              schema_idropotabile_id: evento.schema_idropotabile_id
            })
            .subscribe((resp) => {
              this._loadPsaObject();
            });
        }
      });
  }

  attachNewEvent() {
    const ref = this.dialog.open(AttachEventComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '50%',
      data: { tipologia: this.psaObject.tipologia, associatedEvents: this.events }
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp) {
        this._psaService
          ._attachEventsToEntity({
            entity_id: this.id,
            events: resp
          })
          .subscribe((resp) => {
            this._loadPsaObject();
          });
      }
    });
  }

  goToEditRelEntityEventoRischioAzioneMitigatrice(relEntityEventoRIschioAzioneMitigatrice) {
    const ref = this.dialog.open(UpdateRelActionValueComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '50%',
      data: relEntityEventoRIschioAzioneMitigatrice
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp === 'UPDATED') {
        this._loadPsaObject();
        this.loadIndiceRischio();
        this._mapService.clearLayerCache();
      }
    });
  }

  attachNewAction(evento) {
    const ref = this.dialog.open(AttachActionComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '50%',
      disableClose: true,
      data: {
        id: evento.evento_rischio_id, // id su DB dell'evento
        evento: evento.evento,
        associatedActions: evento.actions
      }
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp) {
        this._psaService
          ._attachActionToEntity({
            rel_entity_evento_rischio_id: evento.id, // id sarebbe rel_entity_evento_rischio_id
            actions: resp
          })
          .subscribe((resp) => this._loadPsaObject());
      }
    });
  }

  deleteAction(azione) {
    this._psaService
      ._removeActionFromEntity({
        rel_entity_evento_rischio_id: azione.rel_entity_evento_rischio_id,
        azione_mitigatrice_id: azione.azione_mitigatrice_id
      })
      .subscribe((resp) => {
        this._loadPsaObject();
      });
  }

  getEventsOfEntity() {
    return this.psaObject.psa.events.map((eventoRel) => ({
      ...eventoRel.evento,
      probabilita: eventoRel?.probabilita
    }));
  }

  getActionsOfEvent(event, actions) {
    return actions.filter((action) => action.rel_entity_evento_rischio_id === event.id);
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  async setOrUnsetWorked(evento) {
    try {
      const body = {
        evento_rischio_id: evento.evento_rischio_id,
        schema_idropotabile_id: evento.schema_idropotabile_id,
        lavorato: !evento.lavorato
      };
      const response = await lastValueFrom(this._psaService._updateRelEvent(body));
      if (!!response.error) {
        this._ebwModalService.openErrorModal({ title: response.errorDescription, content: response.message });
        return;
      }
      evento.lavorato = !evento.lavorato;
      this.loadStatoAvanzamento();
      this._mapService.clearLayerCache();
    } catch (error) {
      this._ebwModalService.openErrorModal({ title: 'Errore', content: error.message });
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  async setOrUnsetActive(domEvent: MatCheckboxChange, evento) {
    try {
      const body = {
        evento_rischio_id: evento.evento_rischio_id,
        schema_idropotabile_id: evento.schema_idropotabile_id,
        attivo: domEvent.checked
      };
      const response = await lastValueFrom(this._psaService._updateRelEvent(body));
      if (!!response.error) {
        this._ebwModalService.openErrorModal({ title: response.errorDescription, content: response.message });
        return;
      }
      evento.attivo = domEvent.checked;
      this.loadIndiceRischio();
      this.loadStatoAvanzamento();
      this._mapService.clearLayerCache();
    } catch (error) {
      this._ebwModalService.openErrorModal({ title: 'Errore', content: error.message });
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  async changeActionState(event, relEntityEventoAzione, stato: 0 | 1 | 2) {
    try {
      const body = {
        id: relEntityEventoAzione.id,
        stato
      };
      const response = await lastValueFrom(this._psaService._updateRelEntityEventoRischioAzioneMitigatrice(body));
      if (response.error) {
        this._ebwModalService.openErrorModal({ title: response.errorDescription, content: response.message });
        return;
      }

      let actions = safeJsonClone(event.actions);
      const actionIndex = actions.findIndex((a) => a.id === relEntityEventoAzione.id);
      if (actionIndex !== -1) {
        actions[actionIndex] = { ...actions[actionIndex], stato };
        event.actions = [...actions]; // Trigger del change detection
      }

      if (stato == 1) {
        this.goToEditRelEntityEventoRischioAzioneMitigatrice(relEntityEventoAzione);
      }
      this.loadIndiceRischio();
      this._mapService.clearLayerCache();
    } catch (error) {
      this._ebwModalService.openErrorModal({ title: 'Errore', content: error.message });
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  goToCheckList() {
    const ref = this.dialog.open(CheckListComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '60%',
      data: this.psaObject.id,
      hasBackdrop: false,
      // evita che il pulsante del menu (all'apertura) e il pulsante Checklist (alla chiusura) restino evidenziati
      autoFocus: false,
      restoreFocus: false
    });

    ref.afterClosed().subscribe();
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  openCreateInterconnectionPanel() {
    this._psaService.openCreateInterconnectionPanel(this.psaObject);
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  async deleteInterconnection() {
    const title = 'Eliminazione interconnessione';
    const content = "Sei sicuro di voler eliminare questa interconnessione? L'operazione sarà irreversibile.";
    const openedModal = this._ebwModalService.openConfirmModal({ title, content }).afterClosed();
    const result = await lastValueFrom(openedModal);

    if (!result) {
      return;
    }

    try {
      const request = this._psaService.deleteInterconnection(this.psaObject.id);
      await lastValueFrom(request);
      const content = 'Operazione eseguita con successo';
      this._ebwModalService.openInfoModal({ title, content });
      this._mapService.clearLayerCache();
      this._destroy$.next();
      this._splitViewService.layoutConfChange({ isEnable: false, halfScreen: true, collapsed: false });
      this._psaService.nodeChanged$.next();
    } catch (error) {
      console.log('[psaObjectViewer] [deleteInterconnection] ', error);
      const content = 'Si è verificato un errore';
      this._ebwModalService.openErrorModal({ title, content });
    }
  }

  datadir;
  // ------------------------------------------------------------------------------------------------------------------------------------------

  modifyNode() {
    // c'è già uno strumento aperto: il dialog non si apre (il registro mostra l'errore). Il rilascio avviene nella destroy del dialog
    if (!this._mapService.acquireMapClick({ id: DetailNodeComponent.CLICK_OWNER_ID, level: 'strong' })) {
      return;
    }
    const ref = this.dialog.open(DetailNodeComponent, {
      width: '50vw',
      maxWidth: '100vw',
      maxHeight: '100%',
      hasBackdrop: false,
      // senza backdrop MatDialog chiuderebbe comunque il dialog al click sulla mappa sottostante
      disableClose: true,
      data: { ...this.psaObject, events: this.events }
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp) {
        this._loadPsaObject(true);
        this._mapService.clearLayerCache();
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  async deleteNode() {
    const title = 'Eliminazione nodo';
    const content = "Confermi l'eliminazione?";
    const openedModal = this._ebwModalService.openConfirmModal({ title, content }).afterClosed();
    const result = await lastValueFrom(openedModal);

    if (!result) {
      return;
    }

    try {
      const request = this._psaService.deleteNode(this.psaObject.id);
      await lastValueFrom(request);
      const content = 'Operazione avvenuta con successo';
      this._ebwModalService.openInfoModal({ title, content });
      this._mapService.clearLayerCache();
      this._mapService.clearAllFeaturesFromTemporaryLayer();
      this._mapService.clearAllFeaturesFromTemporaryLayer(true);
      this._destroy$.next();
      this._splitViewService.layoutConfChange({ isEnable: false, halfScreen: true, collapsed: false });
      this._psaService.nodeChanged$.next();
    } catch (error) {
      console.log('[psaObjectViewer] [deleteNode] ', error);
      const content = error?.error?.message ?? 'Si è verificato un errore';
      this._ebwModalService.openErrorModal({ title, content });
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------

  get hasChecklist(): boolean {
    return isChecklistElementType(this.psaObject?.tipologia, this.checklistElementTypes);
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._psaService.setPsaToBeDuplicated(null);
  }
}
